'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { MdClose, MdMenu } from 'react-icons/md';
import { Button, ButtonType } from '@/components/ui/button';
import { cn } from '@/lib/styleUtils';
import { TenantImage } from '../../../shared/tenant-image';
import { AbmLink } from '../abm-link';
import type { AbmHeaderProps } from './AbmHeader.types';
import {
  desktopNav,
  dropdownLink,
  dropdownPanel,
  groupWrapper,
  headerBar,
  headerInner,
  logoImage,
  logoLink,
  menuToggle,
  mobileGroup,
  mobileGroupLabel,
  mobileLink,
  mobileList,
  mobilePanel,
  mobileSubLink,
  mobileViewAll,
  navLink,
} from './AbmHeader.style';

/** `Find Parking` → `find-parking`, for data-cy values derived from a label. */
const toCy = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/**
 * ABM site header — logo, primary navigation with Industries / Services hover menus, account link.
 *
 * ## Invariants:
 * - The desktop sub-menus are pure CSS (hover + `focus-within`), so a keyboard user tabbing
 *   through a group sees its pages exactly like a mouse user; the group label itself links to the
 *   index page, as on abmparking.com.
 */
export function AbmHeader({
  logoSrc,
  logoSize,
  logoAlt,
  homeHref,
  navAriaLabel,
  openMenuLabel,
  closeMenuLabel,
  newTabLabel,
  leadingLinks,
  groups,
  trailingLinks,
  accountLink,
}: AbmHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const closeMenu = useCallback(() => setMobileOpen(false), []);

  // Escape and a click outside the header close the mobile menu.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    const onPointerDown = (event: PointerEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [mobileOpen, closeMenu]);

  const renderLink = (
    link: AbmHeaderProps['leadingLinks'][number],
    className: string,
    cy: string
  ) => (
    <AbmLink
      key={link.href}
      href={link.href}
      external={link.external}
      newTabLabel={newTabLabel}
      className={className}
      onClick={closeMenu}
      data-cy={cy}
    >
      {link.label}
    </AbmLink>
  );

  return (
    <header ref={headerRef} className={headerBar} data-cy="abm-header">
      <div className={headerInner}>
        <Button
          buttonType={ButtonType.text}
          className={menuToggle}
          aria-label={mobileOpen ? closeMenuLabel : openMenuLabel}
          aria-expanded={mobileOpen}
          aria-controls="abm-mobile-menu"
          onClick={() => setMobileOpen((open) => !open)}
          data-cy="abm-header-menu-toggle"
        >
          {mobileOpen ? (
            <MdClose size={32} aria-hidden="true" />
          ) : (
            <MdMenu size={32} aria-hidden="true" />
          )}
        </Button>

        <AbmLink href={homeHref} onClick={closeMenu} className={logoLink} data-cy="abm-header-logo">
          <TenantImage
            src={logoSrc}
            alt={logoAlt}
            width={logoSize.width}
            height={logoSize.height}
            unoptimized
            className={logoImage}
          />
        </AbmLink>

        <nav aria-label={navAriaLabel} className={desktopNav}>
          {leadingLinks.map((link) =>
            renderLink(link, navLink, `abm-header-link-${toCy(link.label)}`)
          )}
          {groups.map((group) => (
            <div key={group.id} className={groupWrapper}>
              {renderLink(
                { label: group.label, href: group.href },
                navLink,
                `abm-header-group-${group.id}`
              )}
              <div
                id={`abm-nav-${group.id}`}
                className={dropdownPanel}
                data-cy={`abm-header-group-${group.id}-menu`}
              >
                {group.items.map((item) =>
                  renderLink(item, dropdownLink, `abm-header-group-${group.id}-item`)
                )}
              </div>
            </div>
          ))}
          {trailingLinks.map((link) =>
            renderLink(link, navLink, `abm-header-link-${toCy(link.label)}`)
          )}
          {renderLink(accountLink, navLink, 'abm-header-account')}
        </nav>
      </div>

      <nav
        id="abm-mobile-menu"
        aria-label={navAriaLabel}
        className={mobilePanel({ open: mobileOpen })}
        data-cy="abm-header-mobile-menu"
      >
        <div className={mobileList}>
          {leadingLinks.map((link) => renderLink(link, mobileLink, 'abm-mobile-link'))}
          {groups.map((group) => (
            <div key={group.id} className={mobileGroup}>
              <span className={mobileGroupLabel}>{group.label}</span>
              {renderLink(
                { label: group.viewAllLabel, href: group.href },
                cn(mobileSubLink, mobileViewAll),
                'abm-mobile-group-all'
              )}
              {group.items.map((item) => renderLink(item, mobileSubLink, 'abm-mobile-group-item'))}
            </div>
          ))}
          {trailingLinks.map((link) => renderLink(link, mobileLink, 'abm-mobile-link'))}
          {renderLink(accountLink, mobileLink, 'abm-mobile-account')}
        </div>
      </nav>
    </header>
  );
}
